import { useEffect } from "react";
import { match } from "path-to-regexp";
import { useLocation } from "react-router-dom";
import { FreestarPlacements } from "./freestar-placements";
import { FreestarSlot } from "./FreestarSlot";
import { isPremiumUser } from "./utils";

export { FREESTAR_PUBLISHER, FreestarPlacements } from "./freestar-placements";

// Same pages that ran Playwire's tagless units; everything else (legal pages, support-us, ad-whitelist) stays ad-free.
const AD_ROUTES = [
  "/wow",
  "/wow/classes-and-specs",
  "/wow/faq",
  "/wow/tier-list/dps-rankings",
  "/wow/tier-list/dps-rankings/raid",
  "/wow/tier-list/dps-rankings/pvp",
  "/wow/tier-list/tank-rankings",
  "/wow/tier-list/tank-rankings/raid",
  "/wow/tier-list/tank-rankings/pvp",
  "/wow/tier-list/healer-rankings",
  "/wow/tier-list/healer-rankings/raid",
  "/wow/tier-list/healer-rankings/pvp",
  "/wow/:spec/:class/talents",
  "/wow/:spec/:class/talents/raid",
  "/wow/:spec/:class/gear",
  "/wow/:spec/:class/gear/raid",
  "/wow/:spec/:class/gems-and-enchants",
  "/wow/:spec/:class/gems-and-enchants/raid",
  "/wow/:spec/:class/builds",
  "/wow/:spec/:class/builds/raid",
  "/wow/:spec/:class/trinkets",
  "/wow/:spec/:class/trinkets/raid",
  "/wow/:spec/:class/pvp",
].map((route) => match(route));

function freestarQueue(fn: (freestar: FreestarGlobal) => void) {
  window.freestar = window.freestar ?? ({ queue: [] } as unknown as FreestarGlobal);
  window.freestar.queue.push(() => {
    if (window.freestar) fn(window.freestar);
  });
}

// pubfig counts the initial page load itself; module scope so it survives the footer remounting.
let isFirstPageview = true;

// The footer and the floating video player are created one tick late so a redirect that fires in a mount effect
// does not create, delete, and re-create it back to back.
function StickyFooter({ pathname }: { pathname: string }) {
  useEffect(() => {
    let created = false;
    const timer = setTimeout(() => {
      created = true;
      if (!isFirstPageview) freestarQueue((freestar) => freestar.trackPageview());
      isFirstPageview = false;
      freestarQueue((freestar) => freestar.newStickyFooter(FreestarPlacements.BottomRail));
      freestarQueue((freestar) => freestar.newVideo(FreestarPlacements.Video));
    });

    return () => {
      clearTimeout(timer);
      if (created) {
        freestarQueue((freestar) => freestar.deleteVideo(FreestarPlacements.Video));
        freestarQueue((freestar) => freestar.deleteStickyFooter(FreestarPlacements.BottomRail));
      }
    };
  }, [pathname]);

  return null;
}

// Wunderkind's tag, as supplied by Freestar. It only bootstraps tag.bounceexchange.com/<id>/i.js.
// The bx_tracker GAM macro from the supplied tag is left out: outside an ad server it is never
// substituted, and the script ignores it.
function WunderkindTag() {
  useEffect(() => {
    if (document.getElementById("bx-tag")) return;
    const script = document.createElement("script");
    script.id = "bx-tag";
    script.async = true;
    script.charset = "utf-8";
    script.src = "https://assets.bounceexchange.com/assets/bounce/iframebuster.js";
    script.setAttribute("data-website-id", "8903");
    document.head.appendChild(script);
  }, []);

  return null;
}

export const FreestarContainer = () => {
  const { pathname } = useLocation();
  // WoW has no user state query; premium comes from the ugg_premium cookie set by u.gg.
  const isPremium = isPremiumUser();

  // Freestar Recovered checks this flag before showing its ad block prompt.
  // Ad-Shield is configured to skip recovery when uggIsPremium is true.
  useEffect(() => {
    window.disable_fsr = isPremium;
    window.uggIsPremium = isPremium;
  }, [isPremium]);

  if (isPremium) return null;
  if (!AD_ROUTES.some((matches) => matches(pathname))) return null;

  return (
    <>
      <WunderkindTag />
      <StickyFooter pathname={pathname} />
      <FreestarSlot placement={FreestarPlacements.OopFlex} slotId="oop-flex" refreshOnSearch={false} />
    </>
  );
};
